/**
 * Select a resource type.
 */

import {Select} from '@wandb/weave/components/Form/Select';
import {Icon, IconName} from '@wandb/weave/components/Icon';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React from 'react';
import {FormatOptionLabelMeta} from 'react-select';

import {useAdminModeActive} from '../../../util/admin';
import {ResourceType} from '../utilTypes';
import * as S from './SelectResourceType.styles';

type SelectResourceTypeProps = {
  value: string;
  onSelectResourceType: (name: ResourceType) => void;
};

type ResourceTypeOption = {
  icon: IconName;
  value: ResourceType;
  label: string;
  info: string;
  isRecommended?: boolean;
  isAdminOnly?: boolean;
  isDisabled?: boolean;
  ['aria-role']?: string;
};
type GroupedResourceTypeOptions = {
  label: string;
  options: ResourceTypeOption[];
};

const formatOptionLabel = (
  {icon, label, isRecommended, isAdminOnly, info}: ResourceTypeOption,
  {context}: FormatOptionLabelMeta<ResourceTypeOption>
) => (
  <S.ResourceTypeOption>
    <S.ResourceTypeIcon>
      <Icon name={icon} />
    </S.ResourceTypeIcon>
    <S.ResourceTypeOptionLabel>{label}</S.ResourceTypeOptionLabel>
    {context !== 'value' && (
      <>
        <div
          data-test="resource-select-option"
          style={{display: 'flex', alignItems: 'center'}}>
          {isRecommended && <S.Recommended>Recommended</S.Recommended>}
          {isAdminOnly && <S.AdminOnly>W&B admin only</S.AdminOnly>}
          <Tooltip
            content={info}
            trigger={
              <S.ResourceInfo>
                <Icon name="info" />
              </S.ResourceInfo>
            }
          />
        </div>
      </>
    )}
  </S.ResourceTypeOption>
);

const OPTION_LOCAL_CONTAINER: ResourceTypeOption = {
  icon: 'docker-whale',
  value: 'local-container',
  label: 'Docker container',
  isRecommended: true,
  info: 'Runs in this queue will run in a local docker container',
};
const OPTION_LOCAL_PROCESS: ResourceTypeOption = {
  icon: 'laptop-local-computer',
  value: 'local-process',
  label: 'Local process',
  isAdminOnly: true,
  info: 'Runs in this queue will run inside the launch agent process',
};
const OPTION_KUBERNETES: ResourceTypeOption = {
  icon: 'kubernetes',
  value: 'kubernetes',
  label: 'Kubernetes',
  info: 'Runs in this queue will use a Kubernetes cluster.',
};
const OPTION_SAGEMAKER: ResourceTypeOption = {
  icon: 'amazon-sagemaker',
  value: 'sagemaker',
  label: 'Amazon SageMaker',
  info: 'Runs in this queue will run in Amazon SageMaker',
};
const OPTION_VERTEX: ResourceTypeOption = {
  icon: 'vertex-gcp',
  value: 'vertex',
  label: 'GCP Vertex',
  info: 'Runs in this queue will run in GCP Vertex',
};

export const SelectResourceType = ({
  value,
  onSelectResourceType,
}: SelectResourceTypeProps) => {
  const isSiteAdmin = useAdminModeActive();

  // Local Process mode is only intended as a local development aid.
  const OPTIONS_SINGLE_NODE = [OPTION_LOCAL_CONTAINER];
  if (isSiteAdmin) {
    OPTIONS_SINGLE_NODE.push(OPTION_LOCAL_PROCESS);
  }

  const OPTIONS_CLUSTERS = [OPTION_KUBERNETES, OPTION_SAGEMAKER, OPTION_VERTEX];
  const OPTION_MAP: Record<string, ResourceTypeOption> = {};
  OPTIONS_SINGLE_NODE.forEach(o => (OPTION_MAP[o.value] = o));
  OPTIONS_CLUSTERS.forEach(o => (OPTION_MAP[o.value] = o));

  const OPTIONS: GroupedResourceTypeOptions[] = [
    {
      label: 'Single node',
      options: OPTIONS_SINGLE_NODE,
    },
    {
      label: 'Clusters',
      options: OPTIONS_CLUSTERS,
    },
  ];

  const selected = OPTION_MAP[value];

  const onChange = (option: ResourceTypeOption | null) => {
    if (option) {
      onSelectResourceType(option.value);
    }
  };

  return (
    <div data-test="select-resource-type">
      <Select<ResourceTypeOption, false, GroupedResourceTypeOptions>
        options={OPTIONS}
        placeholder="Select a resource type..."
        value={selected}
        formatOptionLabel={formatOptionLabel}
        onChange={onChange}
      />
    </div>
  );
};
